import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiCheckCircle,
  FiCopy,
  FiKey,
  FiPlus,
  FiRefreshCw,
  FiRotateCw,
  FiSave,
  FiTrash2,
} from "react-icons/fi";
import axios from "../../api/axiosInstance";

const PROVIDER = "crm_whatsapp";

const defaultSettings = {
  phone_number_id: "",
  display_phone_number: "",
  whatsapp_business_account_id: "",
  graph_api_version: "v23.0",
  allow_text: true,
  allow_images: true,
  allow_documents: true,
  allow_audio: true,
  allow_video: false,
  assigned_user_id: "",
  is_default_outbound: false,
  default_template_name: "",
  default_template_language: "es_ES",
};

const css = `
  .waCrmPage {
    width: 100%;
    padding: 0 18px 18px;
  }

  .waCrmShell {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: linear-gradient(180deg, rgba(17, 24, 39, 0.96), rgba(6, 12, 20, 0.96));
    color: rgba(244, 250, 255, 0.92);
    overflow: hidden;
  }

  .waCrmHeader {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
    padding: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .waCrmKicker {
    color: rgba(65, 210, 170, 0.82);
    font-size: 0.74rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .waCrmTitle {
    margin: 4px 0 0;
    color: #f4faff;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: 1.38rem;
    font-weight: 900;
  }

  .waCrmTitle svg {
    color: #41d2aa;
  }

  .waCrmSubtitle {
    margin-top: 5px;
    color: rgba(230, 238, 248, 0.68);
    font-size: 0.86rem;
    font-weight: 750;
  }

  .waCrmSummary {
    padding: 14px 18px 0;
  }

  .waCrmSummaryHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .waCrmSummaryHeader h3 {
    margin: 0;
    color: #f4faff;
    font-size: 0.98rem;
    font-weight: 900;
  }

  .waCrmSummaryHeader span {
    color: rgba(230, 238, 248, 0.62);
    font-size: 0.78rem;
    font-weight: 850;
  }

  .waCrmBuList {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
  }

  .waCrmBuCard {
    min-height: 100px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.035);
    color: inherit;
    padding: 11px;
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
  }

  .waCrmBuCard--selected {
    border-color: rgba(65, 210, 170, 0.5);
    background: rgba(65, 210, 170, 0.09);
  }

  .waCrmBuName {
    color: #f4faff;
    font-size: 0.9rem;
    font-weight: 900;
  }

  .waCrmBuFoot {
    color: rgba(230, 238, 248, 0.62);
    font-size: 0.74rem;
    font-weight: 800;
  }

  .waCrmBody {
    display: grid;
    grid-template-columns: minmax(280px, 0.84fr) minmax(0, 1.16fr);
    gap: 14px;
    padding: 18px;
  }

  .waCrmPanel {
    border-radius: 13px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.04);
    padding: 14px;
  }

  .waCrmPanel + .waCrmPanel {
    margin-top: 12px;
  }

  .waCrmPanel h3 {
    margin: 0 0 11px;
    color: #f4faff;
    font-size: 0.98rem;
    font-weight: 900;
  }

  .waCrmGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 11px;
  }

  .waCrmField,
  .waCrmToggle {
    min-width: 0;
  }

  .waCrmField {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .waCrmField--wide {
    grid-column: 1 / -1;
  }

  .waCrmField span,
  .waCrmToggle span {
    color: rgba(230, 238, 248, 0.68);
    font-size: 0.78rem;
    font-weight: 850;
  }

  .waCrmField input,
  .waCrmField select {
    width: 100%;
    min-height: 40px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    background: rgba(255, 255, 255, 0.055);
    color: #fff;
    padding: 0 10px;
    font: inherit;
    font-weight: 800;
    outline: none;
  }

  .waCrmField option {
    color: #111827;
    background: #fff;
  }

  .waCrmMono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    overflow-wrap: anywhere;
  }

  .waCrmToggle {
    min-height: 40px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.035);
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 0 10px;
  }

  .waCrmToggle input {
    width: 16px;
    height: 16px;
  }

  .waCrmActions {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 13px;
  }

  .waCrmButton {
    min-height: 38px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.13);
    background: rgba(255, 255, 255, 0.055);
    color: rgba(244, 250, 255, 0.92);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 12px;
    font: inherit;
    font-weight: 850;
    cursor: pointer;
  }

  .waCrmButton--primary {
    color: #07130e;
    background: #41d2aa;
    border-color: rgba(65, 210, 170, 0.6);
  }

  .waCrmButton--danger {
    color: #ffdada;
    border-color: rgba(255, 93, 93, 0.32);
    background: rgba(255, 93, 93, 0.11);
  }

  .waCrmButton:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .waCrmBadge {
    min-height: 25px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.11);
    padding: 4px 8px;
    display: inline-flex;
    align-items: center;
    color: rgba(244, 250, 255, 0.9);
    background: rgba(255, 255, 255, 0.045);
    font-size: 0.74rem;
    font-weight: 900;
    text-transform: capitalize;
  }

  .waCrmBadge--active,
  .waCrmBadge--configured,
  .waCrmBadge--message_created,
  .waCrmBadge--processed,
  .waCrmBadge--valid {
    color: #cbfff0;
    border-color: rgba(65, 210, 170, 0.34);
    background: rgba(65, 210, 170, 0.13);
  }

  .waCrmBadge--draft,
  .waCrmBadge--missing,
  .waCrmBadge--duplicate,
  .waCrmBadge--ignored {
    color: #ffe7ad;
    border-color: rgba(255, 210, 105, 0.34);
    background: rgba(255, 210, 105, 0.12);
  }

  .waCrmBadge--disabled,
  .waCrmBadge--invalid,
  .waCrmBadge--failed {
    color: #ffd6d6;
    border-color: rgba(255, 93, 93, 0.32);
    background: rgba(255, 93, 93, 0.10);
  }

  .waCrmState {
    border-radius: 10px;
    padding: 10px 11px;
    margin-bottom: 12px;
    font-weight: 800;
    line-height: 1.35;
  }

  .waCrmState--info {
    color: #d9fff5;
    border: 1px solid rgba(65, 210, 170, 0.22);
    background: rgba(65, 210, 170, 0.075);
  }

  .waCrmState--error {
    color: #ffd6d6;
    border: 1px solid rgba(255, 93, 93, 0.32);
    background: rgba(130, 26, 34, 0.24);
  }

  .waCrmTableShell {
    overflow: auto;
    border-radius: 11px;
    border: 1px solid rgba(255, 255, 255, 0.09);
  }

  .waCrmTable {
    width: 100%;
    min-width: 650px;
    border-collapse: collapse;
  }

  .waCrmTable th,
  .waCrmTable td {
    padding: 10px 11px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    vertical-align: top;
  }

  .waCrmTable th {
    color: rgba(230, 238, 248, 0.62);
    font-size: 0.72rem;
    text-transform: uppercase;
    font-weight: 900;
    background: rgba(255, 255, 255, 0.035);
  }

  .waCrmTable td {
    color: rgba(230, 238, 248, 0.88);
    font-size: 0.82rem;
  }

  .waCrmEmpty {
    min-height: 96px;
    display: grid;
    place-items: center;
    color: rgba(230, 238, 248, 0.58);
    font-weight: 850;
    text-align: center;
    padding: 14px;
  }

  @media (max-width: 920px) {
    .waCrmHeader,
    .waCrmBody {
      grid-template-columns: 1fr;
    }

    .waCrmHeader {
      align-items: flex-start;
      flex-direction: column;
    }

    .waCrmGrid {
      grid-template-columns: 1fr;
    }
  }
`;

function getApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.message) return data.message;
  if (err?.response?.status) return `${fallback} (HTTP ${err.response.status})`;
  if (err?.message) return `${fallback}: ${err.message}`;
  return fallback;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function normalizeBusinessUnitIds(value) {
  if (Array.isArray(value)) {
    return value.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => Number(String(item).trim()))
      .filter((item) => Number.isInteger(item) && item > 0);
  }
  return [];
}

function userBelongsToBusinessUnit(user, businessUnitId) {
  const cleanBusinessUnitId = Number(businessUnitId);
  if (!Number.isInteger(cleanBusinessUnitId) || cleanBusinessUnitId <= 0) return false;
  if (!user || user.is_active === false) return false;
  if (user.has_access_to_all_business_units) return true;
  if (normalizeBusinessUnitIds(user.business_unit_ids).includes(cleanBusinessUnitId)) return true;
  return Array.isArray(user.role_assignments)
    ? user.role_assignments.some((assignment) => Number(assignment?.business_unit_id) === cleanBusinessUnitId)
    : false;
}

function getUserLabel(user) {
  return user?.name || user?.email || `User ${user?.id}`;
}

function buildSettingsPayload(settings) {
  return {
    phone_number_id: String(settings.phone_number_id || "").trim(),
    display_phone_number: String(settings.display_phone_number || "").trim(),
    whatsapp_business_account_id: String(settings.whatsapp_business_account_id || "").trim(),
    graph_api_version: String(settings.graph_api_version || "v23.0").trim(),
    allow_text: Boolean(settings.allow_text),
    allow_images: Boolean(settings.allow_images),
    allow_documents: Boolean(settings.allow_documents),
    allow_audio: Boolean(settings.allow_audio),
    allow_video: Boolean(settings.allow_video),
    assigned_user_id: settings.assigned_user_id || null,
    is_default_outbound: Boolean(settings.is_default_outbound),
    default_template_name: String(settings.default_template_name || "").trim(),
    default_template_language: String(settings.default_template_language || "es_ES").trim(),
  };
}

function Badge({ value }) {
  return <span className={`waCrmBadge waCrmBadge--${String(value || "").toLowerCase()}`}>{value || "-"}</span>;
}

export default function WhatsAppCrmIntegrations() {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState("");
  const [selectedIntegrationId, setSelectedIntegrationId] = useState("");
  const [integrationName, setIntegrationName] = useState("WhatsApp CRM");
  const [status, setStatus] = useState("draft");
  const [settings, setSettings] = useState(defaultSettings);
  const [accessToken, setAccessToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deletingToken, setDeletingToken] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const businessUnitNameById = useMemo(() => {
    const map = new Map();
    businessUnits.forEach((businessUnit) => {
      map.set(String(businessUnit.id), businessUnit.name);
    });
    return map;
  }, [businessUnits]);

  const selectedIntegration = useMemo(
    () =>
      integrations.find(
        (integration) => String(integration.id) === String(selectedIntegrationId)
      ) || null,
    [integrations, selectedIntegrationId]
  );

  const configuredIntegrations = useMemo(
    () =>
      [...integrations].sort((a, b) => {
        const aName = a.business_unit?.name || businessUnitNameById.get(String(a.business_unit_id)) || "";
        const bName = b.business_unit?.name || businessUnitNameById.get(String(b.business_unit_id)) || "";
        if (aName !== bName) return aName.localeCompare(bName);
        return Number(a.id || 0) - Number(b.id || 0);
      }),
    [businessUnitNameById, integrations]
  );

  const userNameById = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      map.set(String(user.id), getUserLabel(user));
    });
    return map;
  }, [users]);

  const assignedUsersForSelectedBusinessUnit = useMemo(
    () => users.filter((user) => userBelongsToBusinessUnit(user, selectedBusinessUnitId)),
    [selectedBusinessUnitId, users]
  );

  const loadLogs = useCallback(async (integrationId) => {
    if (!integrationId) {
      setLogs([]);
      return;
    }

    try {
      const response = await axios.get(`/api/admin/crm-provider-integrations/${integrationId}/webhook-logs`, {
        params: { limit: 30 },
      });
      setLogs(Array.isArray(response.data?.rows) ? response.data.rows : []);
    } catch (err) {
      console.error("[Admin] WhatsApp CRM logs load error:", err);
      setLogs([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [businessUnitsResponse, integrationsResponse, usersResponse] = await Promise.all([
        axios.get("/api/business-units/all"),
        axios.get("/api/admin/crm-provider-integrations", { params: { provider: PROVIDER } }),
        axios.get("/api/admin/crm-provider-integrations/users"),
      ]);

      const nextBusinessUnits = Array.isArray(businessUnitsResponse.data)
        ? businessUnitsResponse.data
        : [];
      const nextIntegrations = Array.isArray(integrationsResponse.data?.rows)
        ? integrationsResponse.data.rows
        : [];
      const nextUsers = Array.isArray(usersResponse.data) ? usersResponse.data : [];

      setBusinessUnits(nextBusinessUnits);
      setIntegrations(nextIntegrations);
      setUsers(nextUsers.filter((user) => user?.is_active !== false));
      setSelectedBusinessUnitId((current) => {
        if (current) return current;
        return String(nextIntegrations[0]?.business_unit_id || nextBusinessUnits[0]?.id || "");
      });
      setSelectedIntegrationId((current) => {
        if (current && nextIntegrations.some((integration) => String(integration.id) === String(current))) {
          return current;
        }
        return String(nextIntegrations[0]?.id || "");
      });
    } catch (err) {
      console.error("[Admin] WhatsApp CRM integration load error:", err);
      setError(getApiErrorMessage(err, "Could not load WhatsApp CRM integrations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedIntegration) {
      setSelectedBusinessUnitId(String(selectedIntegration.business_unit_id || ""));
      setIntegrationName(selectedIntegration.name || "WhatsApp CRM");
      setStatus(selectedIntegration.status || "draft");
      setSettings({ ...defaultSettings, ...(selectedIntegration.settings || {}) });
      loadLogs(selectedIntegration.id);
    } else {
      setIntegrationName("WhatsApp CRM");
      setStatus("draft");
      setSettings(defaultSettings);
      setLogs([]);
    }
    setAccessToken("");
  }, [loadLogs, selectedIntegration]);

  useEffect(() => {
    if (!users.length || !selectedBusinessUnitId || !settings.assigned_user_id) return;
    const assignedUser = users.find((user) => String(user.id) === String(settings.assigned_user_id));
    if (userBelongsToBusinessUnit(assignedUser, selectedBusinessUnitId)) return;
    setSettings((prev) => ({ ...prev, assigned_user_id: "" }));
  }, [selectedBusinessUnitId, settings.assigned_user_id, users]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const selectBusinessUnit = (businessUnitId) => {
    setSelectedBusinessUnitId(String(businessUnitId));
    setSelectedIntegrationId("");
    setError("");
    setMessage("");
  };

  const selectIntegration = (integration) => {
    setSelectedBusinessUnitId(String(integration.business_unit_id || ""));
    setSelectedIntegrationId(String(integration.id || ""));
    setError("");
    setMessage("");
  };

  const startNewIntegration = () => {
    setSelectedIntegrationId("");
    setIntegrationName("WhatsApp CRM");
    setStatus("draft");
    setSettings(defaultSettings);
    setAccessToken("");
    setLogs([]);
    setError("");
    setMessage("");
  };

  const saveIntegration = async () => {
    if (!selectedBusinessUnitId) return;
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        business_unit_id: selectedBusinessUnitId,
        name: integrationName || "WhatsApp CRM",
        status,
        settings: buildSettingsPayload(settings),
      };

      const response = selectedIntegration?.id
        ? await axios.patch(`/api/admin/crm-provider-integrations/${selectedIntegration.id}`, payload)
        : await axios.post("/api/admin/crm-provider-integrations/whatsapp-crm", payload);

      setIntegrations((prev) => [
        ...prev.filter((item) => item.id !== response.data.id),
        response.data,
      ]);
      setSelectedIntegrationId(String(response.data.id || ""));
      setMessage("WhatsApp CRM settings saved.");
      await loadData();
    } catch (err) {
      console.error("[Admin] WhatsApp CRM save error:", err);
      setError(getApiErrorMessage(err, "Could not save WhatsApp CRM settings."));
    } finally {
      setSaving(false);
    }
  };

  const saveAccessToken = async () => {
    if (!selectedIntegration?.id) {
      setError("Prepare this Business Unit before saving the Meta access token.");
      return;
    }
    if (!accessToken.trim()) {
      setError("Meta access token is required.");
      return;
    }

    setTokenSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`,
        {
          access_token: accessToken,
          settings: buildSettingsPayload(settings),
        }
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setSettings({ ...defaultSettings, ...(response.data.settings || {}) });
      setAccessToken("");
      setMessage("Meta access token saved.");
    } catch (err) {
      console.error("[Admin] WhatsApp CRM token save error:", err);
      setError(getApiErrorMessage(err, "Could not save Meta access token."));
    } finally {
      setTokenSaving(false);
    }
  };

  const deleteAccessToken = async () => {
    if (!selectedIntegration?.id) return;
    const confirmed = window.confirm("Remove saved Meta access token for this Business Unit?");
    if (!confirmed) return;

    setDeletingToken(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.delete(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/credentials`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setMessage("Meta access token removed.");
    } catch (err) {
      console.error("[Admin] WhatsApp CRM token delete error:", err);
      setError(getApiErrorMessage(err, "Could not remove Meta access token."));
    } finally {
      setDeletingToken(false);
    }
  };

  const testConnection = async () => {
    if (!selectedIntegration?.id) return;

    setTesting(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/test-connection`,
        { settings: buildSettingsPayload(settings) }
      );
      const phone = response.data?.display_phone_number || response.data?.phone_number_id || "phone number";
      const name = response.data?.verified_name ? ` (${response.data.verified_name})` : "";
      setMessage(`WhatsApp Cloud API connection OK for ${phone}${name}.`);
      await loadData();
    } catch (err) {
      console.error("[Admin] WhatsApp CRM test error:", err);
      setError(getApiErrorMessage(err, "Could not test WhatsApp Cloud API connection."));
      await loadData();
    } finally {
      setTesting(false);
    }
  };

  const rotateWebhookToken = async () => {
    if (!selectedIntegration?.id) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `/api/admin/crm-provider-integrations/${selectedIntegration.id}/rotate-webhook-token`
      );
      setIntegrations((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
      setMessage("Verify token rotated.");
    } catch (err) {
      console.error("[Admin] WhatsApp CRM token rotate error:", err);
      setError(getApiErrorMessage(err, "Could not rotate verify token."));
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setMessage(`${label} copied.`);
    } catch (_err) {
      setMessage(`${label} is ready to copy manually.`);
    }
  };

  return (
    <div className="waCrmPage">
      <style>{css}</style>
      <div className="waCrmShell">
        <div className="waCrmHeader">
          <div>
            <div className="waCrmKicker">CRM integrations</div>
            <h2 className="waCrmTitle">
              <FaWhatsapp aria-hidden="true" />
              <span>WhatsApp CRM</span>
            </h2>
            <div className="waCrmSubtitle">Business Unit chat routing for CRM conversations.</div>
          </div>
          <div className="waCrmActions" style={{ marginTop: 0 }}>
            <button type="button" className="waCrmButton" onClick={startNewIntegration}>
              <FiPlus aria-hidden="true" />
              <span>New number</span>
            </button>
            <button type="button" className="waCrmButton" onClick={loadData} disabled={loading}>
              <FiRefreshCw aria-hidden="true" />
              <span>{loading ? "Loading" : "Refresh"}</span>
            </button>
          </div>
        </div>

        <div className="waCrmSummary">
          <div className="waCrmSummaryHeader">
            <h3>Business Units with WhatsApp CRM</h3>
            <span>{configuredIntegrations.length} configured</span>
          </div>
          <div className="waCrmBuList">
            {configuredIntegrations.length ? (
              configuredIntegrations.map((integration) => (
                <button
                  key={integration.id}
                  type="button"
                  className={`waCrmBuCard ${
                    String(selectedIntegrationId) === String(integration.id)
                      ? "waCrmBuCard--selected"
                      : ""
                  }`}
                  onClick={() => selectIntegration(integration)}
                >
                  <div>
                    <div className="waCrmBuName">
                      {integration.name || "WhatsApp CRM"}
                    </div>
                    <div className="waCrmBuFoot" style={{ marginTop: 4 }}>
                      {integration.business_unit?.name || businessUnitNameById.get(String(integration.business_unit_id)) || `BU ${integration.business_unit_id}`}
                    </div>
                    <div className="waCrmBuFoot" style={{ marginTop: 4 }}>
                      {integration.settings?.display_phone_number || integration.settings?.phone_number_id || "No phone number ID"}
                      {integration.settings?.assigned_user_id
                        ? ` · ${userNameById.get(String(integration.settings.assigned_user_id)) || `User ${integration.settings.assigned_user_id}`}`
                        : ""}
                    </div>
                    <div className="waCrmActions" style={{ marginTop: 8 }}>
                      <Badge value={integration.status} />
                      <Badge value={integration.credentials_status} />
                      {integration.settings?.is_default_outbound ? <Badge value="default" /> : null}
                    </div>
                  </div>
                  <div className="waCrmBuFoot">
                    Last event: {integration.last_event_type || "none yet"}
                  </div>
                </button>
              ))
            ) : (
              <div className="waCrmEmpty">No WhatsApp CRM integrations prepared yet.</div>
            )}
          </div>
        </div>

        <div className="waCrmBody">
          <section>
            {error ? <div className="waCrmState waCrmState--error">{error}</div> : null}
            {message ? <div className="waCrmState waCrmState--info">{message}</div> : null}

            <div className="waCrmPanel">
              <h3>Configuration</h3>
              <div className="waCrmGrid">
                <label className="waCrmField waCrmField--wide">
                  <span>Business Unit</span>
                  <select value={selectedBusinessUnitId} onChange={(event) => selectBusinessUnit(event.target.value)}>
                    <option value="">Select Business Unit</option>
                    {businessUnits.map((businessUnit) => (
                      <option key={businessUnit.id} value={businessUnit.id}>
                        {businessUnit.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="waCrmField waCrmField--wide">
                  <span>Integration name</span>
                  <input value={integrationName} onChange={(event) => setIntegrationName(event.target.value)} />
                </label>

                <label className="waCrmField">
                  <span>Status</span>
                  <select value={status} onChange={(event) => setStatus(event.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </label>

                <label className="waCrmField">
                  <span>Graph version</span>
                  <input value={settings.graph_api_version} onChange={(event) => updateSetting("graph_api_version", event.target.value)} />
                </label>

                <label className="waCrmField waCrmField--wide">
                  <span>Assigned commercial</span>
                  <select
                    value={settings.assigned_user_id || ""}
                    onChange={(event) => updateSetting("assigned_user_id", event.target.value)}
                  >
                    <option value="">No assigned user</option>
                    {assignedUsersForSelectedBusinessUnit.length ? null : (
                      <option value="" disabled>
                        No users assigned to this Business Unit
                      </option>
                    )}
                    {assignedUsersForSelectedBusinessUnit.map((user) => (
                      <option key={user.id} value={user.id}>
                        {getUserLabel(user)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="waCrmField">
                  <span>Phone number ID</span>
                  <input value={settings.phone_number_id} onChange={(event) => updateSetting("phone_number_id", event.target.value)} />
                </label>

                <label className="waCrmField">
                  <span>Display phone</span>
                  <input value={settings.display_phone_number} onChange={(event) => updateSetting("display_phone_number", event.target.value)} />
                </label>

                <label className="waCrmField waCrmField--wide">
                  <span>WhatsApp Business Account ID</span>
                  <input value={settings.whatsapp_business_account_id} onChange={(event) => updateSetting("whatsapp_business_account_id", event.target.value)} />
                </label>

                <label className="waCrmField">
                  <span>Default template</span>
                  <input value={settings.default_template_name} onChange={(event) => updateSetting("default_template_name", event.target.value)} />
                </label>

                <label className="waCrmField">
                  <span>Template language</span>
                  <input value={settings.default_template_language} onChange={(event) => updateSetting("default_template_language", event.target.value)} />
                </label>

                <label className="waCrmToggle">
                  <input type="checkbox" checked={settings.is_default_outbound} onChange={(event) => updateSetting("is_default_outbound", event.target.checked)} />
                  <span>Default outbound number</span>
                </label>

                <label className="waCrmToggle">
                  <input type="checkbox" checked={settings.allow_text} onChange={(event) => updateSetting("allow_text", event.target.checked)} />
                  <span>Text</span>
                </label>

                <label className="waCrmToggle">
                  <input type="checkbox" checked={settings.allow_images} onChange={(event) => updateSetting("allow_images", event.target.checked)} />
                  <span>Images</span>
                </label>

                <label className="waCrmToggle">
                  <input type="checkbox" checked={settings.allow_documents} onChange={(event) => updateSetting("allow_documents", event.target.checked)} />
                  <span>Documents</span>
                </label>

                <label className="waCrmToggle">
                  <input type="checkbox" checked={settings.allow_audio} onChange={(event) => updateSetting("allow_audio", event.target.checked)} />
                  <span>Audio</span>
                </label>
              </div>

              <div className="waCrmActions">
                <button type="button" className="waCrmButton waCrmButton--primary" onClick={saveIntegration} disabled={saving || !selectedBusinessUnitId}>
                  <FiSave aria-hidden="true" />
                  <span>{saving ? "Saving" : "Save settings"}</span>
                </button>
                <button type="button" className="waCrmButton" onClick={testConnection} disabled={testing || !selectedIntegration?.id}>
                  <FiCheckCircle aria-hidden="true" />
                  <span>{testing ? "Testing" : "Test connection"}</span>
                </button>
              </div>
            </div>

            <div className="waCrmPanel">
              <h3>Meta access token</h3>
              <div className="waCrmGrid">
                <label className="waCrmField waCrmField--wide">
                  <span>Access token</span>
                  <input
                    type="password"
                    value={accessToken}
                    onChange={(event) => setAccessToken(event.target.value)}
                    placeholder={selectedIntegration?.has_credentials ? "Saved token is hidden" : ""}
                  />
                </label>
              </div>
              <div className="waCrmActions">
                <button type="button" className="waCrmButton waCrmButton--primary" onClick={saveAccessToken} disabled={tokenSaving || !selectedIntegration?.id}>
                  <FiKey aria-hidden="true" />
                  <span>{tokenSaving ? "Saving" : "Save token"}</span>
                </button>
                <button type="button" className="waCrmButton waCrmButton--danger" onClick={deleteAccessToken} disabled={deletingToken || !selectedIntegration?.has_credentials}>
                  <FiTrash2 aria-hidden="true" />
                  <span>{deletingToken ? "Removing" : "Remove token"}</span>
                </button>
              </div>
            </div>
          </section>

          <aside>
            <div className="waCrmPanel">
              <h3>Webhook</h3>
              <div className="waCrmGrid">
                <label className="waCrmField waCrmField--wide">
                  <span>Callback URL</span>
                  <input className="waCrmMono" value={selectedIntegration?.webhook_url || ""} readOnly />
                </label>
                <label className="waCrmField waCrmField--wide">
                  <span>Verify token</span>
                  <input className="waCrmMono" value={selectedIntegration?.webhook_token || ""} readOnly />
                </label>
              </div>
              <div className="waCrmActions">
                <button type="button" className="waCrmButton" onClick={() => copyText(selectedIntegration?.webhook_url, "Webhook URL")} disabled={!selectedIntegration?.webhook_url}>
                  <FiCopy aria-hidden="true" />
                  <span>Copy URL</span>
                </button>
                <button type="button" className="waCrmButton" onClick={() => copyText(selectedIntegration?.webhook_token, "Verify token")} disabled={!selectedIntegration?.webhook_token}>
                  <FiCopy aria-hidden="true" />
                  <span>Copy token</span>
                </button>
                <button type="button" className="waCrmButton" onClick={rotateWebhookToken} disabled={!selectedIntegration?.id || saving}>
                  <FiRotateCw aria-hidden="true" />
                  <span>Rotate token</span>
                </button>
              </div>
            </div>

            <div className="waCrmPanel">
              <h3>Recent events</h3>
              <div className="waCrmTableShell">
                <table className="waCrmTable">
                  <thead>
                    <tr>
                      <th>Received</th>
                      <th>Event</th>
                      <th>Status</th>
                      <th>Signature</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length ? (
                      logs.map((log) => (
                        <tr key={log.id}>
                          <td>{formatDateTime(log.created_at)}</td>
                          <td>{log.event_type || "-"}</td>
                          <td><Badge value={log.status} /></td>
                          <td><Badge value={log.signature_status} /></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>
                          <div className="waCrmEmpty">
                            {selectedIntegration?.id ? "No webhook events yet." : "Prepare a Business Unit to see events."}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
